import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Bell, X, Sparkles, Check } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Campanita de notificaciones del cliente.
 * - Hace polling cada 60s a /api/me/notifications
 * - Muestra contador de no leídas
 * - Panel desplegable con lista de notificaciones
 * - Marca como leída al hacer click
 */
const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const wrapperRef = useRef(null);

  useEffect(() => {
    fetchNotifs();
    const interval = setInterval(fetchNotifs, 60000); // 1 minuto
    return () => clearInterval(interval);
  }, []);

  // Cerrar al hacer click fuera
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchNotifs = async () => {
    try {
      const res = await axios.get(`${API}/me/notifications`);
      setNotifs(res.data.notifications || []);
      setUnread(res.data.unread || 0);
    } catch (e) {
      // cliente sin permisos o error: silencioso
    } finally {
      setLoading(false);
    }
  };

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/me/notifications/${id}/read`);
      setNotifs(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch (e) { /* ignore */ }
  };

  const markAllRead = async () => {
    const unreadIds = notifs.filter(n => !n.read).map(n => n.id);
    await Promise.all(unreadIds.map(id =>
      axios.post(`${API}/me/notifications/${id}/read`).catch(() => {})
    ));
    fetchNotifs();
  };

  if (loading) return null;

  return (
    <div className="relative" ref={wrapperRef}>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(!open)}
        className="relative"
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 max-h-96 overflow-y-auto bg-card border border-border rounded-lg shadow-lg z-[100]" data-testid="notification-panel">
          <div className="flex items-center justify-between p-3 border-b border-border sticky top-0 bg-card">
            <p className="font-semibold text-sm">Notificaciones</p>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={markAllRead} data-testid="mark-all-read">
                  <Check className="h-3 w-3 mr-1" /> Marcar todo
                </Button>
              )}
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {notifs.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-30" />
              Sin notificaciones por ahora
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {notifs.map(n => (
                <li
                  key={n.id}
                  className={`p-3 text-sm hover:bg-zinc-900/50 cursor-pointer transition-colors ${n.read ? 'opacity-70' : ''}`}
                  onClick={() => !n.read && markRead(n.id)}
                  data-testid={`notif-${n.id}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="w-2 h-2 bg-primary rounded-full mt-1.5 flex-shrink-0" />}
                    {n.read && <span className="w-2 h-2 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        {n.type === 'pildora' && <Sparkles className="h-3 w-3 text-amber-400 flex-shrink-0" />}
                        <p className="font-medium text-foreground text-xs truncate">{n.title}</p>
                      </div>
                      <div className="text-xs text-muted-foreground line-clamp-3 whitespace-pre-wrap leading-relaxed">
                        {(n.body || '').split('\n\n').map((paragraph, idx) => (
                          <p key={idx} className={idx > 0 ? 'mt-1' : ''}>
                            {paragraph.split('**').map((part, j) =>
                              j % 2 === 1
                                ? <strong key={j} className="text-emerald-300">{part}</strong>
                                : part
                            )}
                          </p>
                        ))}
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {n.created_at ? new Date(n.created_at).toLocaleString('es-ES') : ''}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
